/**
 * Overview tab's org drill-down trend chart — one line per department, team,
 * or subteam (whichever level the active filter resolves to), matching the
 * prototype's actual behavior: confirmed against
 * finn_absence_dashboard.html's renderOverview(), the grouping is driven by
 * how deep the active department/team filter goes, not by clicking the
 * chart itself, and it's one chart whose grouping changes — not separate
 * charts per level.
 *
 * Split into its own file (out of AbsenceInsightsCharts.tsx) to keep that
 * file under the repo's max-lines lint limit.
 */
import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import type { AbsenceType, AbsenceOrgMonthlySeries } from '../services/absenceApi';
import { pickRatePct, TogglePill, EmptyChartCard, type OrgScopeLevel } from './AbsenceInsightsCharts';
import { InfoTooltip } from './InfoTooltip';

// Idempotent — safe even though AbsenceInsightsCharts.tsx also registers
// these; avoids relying on import order between the two files.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

// Categorical palette for the per-entity lines — all real FINN tokens
// (trustedBlue, orange, green, red, asphalt, loyaltyGold, iron), cycled by
// index when there are more entities than colors, same approach the
// prototype uses with its own DEPT_COLS array.
const ORG_TREND_COLORS = ['#0072EA', '#E88F3D', '#26BC58', '#E71D3F', '#484848', '#C8AB78', '#707070'];

function orgEntityKey(e: AbsenceOrgMonthlySeries): string {
  return [e.department, e.team, e.subteam].filter(Boolean).join('||');
}

function orgEntityLabel(e: AbsenceOrgMonthlySeries, scopeLevel: OrgScopeLevel): string {
  if (scopeLevel === 'subteam') return e.subteam ?? e.department;
  if (scopeLevel === 'team') return e.team ?? e.department;
  return e.department;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function OrgTrendChart({
  currentByDept,
  priorByDept,
  currentByTeam,
  priorByTeam,
  currentBySubteam,
  priorBySubteam,
  scopeLevel,
  absenceType,
}: {
  currentByDept: AbsenceOrgMonthlySeries[];
  priorByDept: AbsenceOrgMonthlySeries[];
  currentByTeam: AbsenceOrgMonthlySeries[];
  priorByTeam: AbsenceOrgMonthlySeries[];
  currentBySubteam: AbsenceOrgMonthlySeries[];
  priorBySubteam: AbsenceOrgMonthlySeries[];
  scopeLevel: OrgScopeLevel;
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);
  // Which entities (by label) the user has hidden via the legend — tracked in
  // React state rather than relying on Chart.js's own per-dataset-index
  // hidden tracking. That tracking is keyed by array position, and toggling
  // "Compare last year" rebuilds the whole datasets array with every
  // entity's prior-year line inserted right after its current one, shifting
  // every later entity's index — which silently scrambled which lines
  // stayed hidden. Driving `hidden` off entity label on every render sidesteps
  // that entirely.
  const [hiddenLabels, setHiddenLabels] = useState<Set<string>>(new Set());

  const { current, prior, heading } =
    scopeLevel === 'subteam'
      ? { current: currentBySubteam, prior: priorBySubteam, heading: 'Monthly Absence Rate by Subteam' }
      : scopeLevel === 'team'
        ? { current: currentByTeam, prior: priorByTeam, heading: 'Monthly Absence Rate by Team' }
        : { current: currentByDept, prior: priorByDept, heading: 'Monthly Absence Rate by Department' };

  if (current.length === 0) {
    return <EmptyChartCard title={heading} message="No data in the selected window." />;
  }

  // Union of months across all entities, not entity[0]'s own months — an
  // entity that didn't exist yet in an earlier month would otherwise shift
  // every later month one slot to the left relative to entities with full
  // coverage, silently misaligning the x-axis.
  const monthOrder = [...new Set(current.flatMap((e) => e.points.map((p) => `${p.year}-${p.month}`)))].sort();
  const monthLabelFor = new Map(
    current.flatMap((e) => e.points).map((p) => [`${p.year}-${p.month}`, p.monthLabel])
  );
  const labels = monthOrder.map((k) => monthLabelFor.get(k) ?? k);

  const priorByEntity = new Map(prior.map((e) => [orgEntityKey(e), e]));
  const datasets: import('chart.js').ChartDataset<'line'>[] = [];
  // Parallel to `datasets`, same push order — lets the tooltip callback
  // distinguish a GDPR-suppressed point from a genuinely missing one for
  // the exact dataset/point under the cursor.
  const suppressedByDataset: boolean[][] = [];

  current.forEach((entity, i) => {
    const color = ORG_TREND_COLORS[i % ORG_TREND_COLORS.length];
    const label = orgEntityLabel(entity, scopeLevel);
    const hidden = hiddenLabels.has(label);
    const pointsByMonth = new Map(entity.points.map((p) => [`${p.year}-${p.month}`, p]));

    datasets.push({
      label,
      hidden,
      data: monthOrder.map((k) => {
        const p = pointsByMonth.get(k);
        return p ? pickRatePct(p, absenceType) : null;
      }),
      borderColor: color,
      backgroundColor: 'transparent',
      tension: 0.35,
      pointRadius: 3,
      pointHoverRadius: 5,
      borderWidth: 2,
      spanGaps: false,
    });
    suppressedByDataset.push(monthOrder.map((k) => pointsByMonth.get(k)?.suppressed ?? false));

    if (showPrior) {
      const priorEntity = priorByEntity.get(orgEntityKey(entity));
      const priorPointsByMonth = new Map((priorEntity?.points ?? []).map((p) => [p.month, p]));
      datasets.push({
        label: `${label} (last year)`,
        // Same hidden state as the current-year line above — a department
        // hidden via the legend stays hidden in both years, regardless of
        // when "Compare last year" gets toggled.
        hidden,
        // Aligned by calendar month number against the CURRENT month axis
        // (prior data is a different year), same approach as the trend chart.
        data: monthOrder.map((k) => {
          const month = Number(k.split('-')[1]);
          const p = priorPointsByMonth.get(month);
          return p ? pickRatePct(p, absenceType) : null;
        }),
        borderColor: hexToRgba(color, 0.45),
        backgroundColor: 'transparent',
        borderDash: [5, 4],
        tension: 0.35,
        pointRadius: 2,
        borderWidth: 1.5,
        spanGaps: false,
      });
      suppressedByDataset.push(
        monthOrder.map((k) => {
          const month = Number(k.split('-')[1]);
          return priorPointsByMonth.get(month)?.suppressed ?? false;
        })
      );
    }
  });

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            {heading}
            <InfoTooltip text="Monthly absence rate (%) per department/team/subteam — total absence days ÷ total available working days for that entity and month. The level shown narrows automatically as you apply Department/Team filters; click a legend entry to show or hide one line." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">Absence rate (%) — click legend to show/hide</p>
        </div>
        <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
          Compare previous year
        </TogglePill>
      </div>
      <div className="mt-4" style={{ height: 280 }}>
        <Line
          data={{ labels, datasets }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const suppressed = suppressedByDataset[context.datasetIndex]?.[context.dataIndex];
                    if (suppressed) return `${context.dataset.label}: Hidden for privacy reasons`;
                    return `${context.dataset.label}: ${context.formattedValue}%`;
                  },
                },
              },
              legend: {
                position: 'bottom',
                labels: { boxWidth: 10, filter: (item) => !item.text.includes('(last year)') },
                // Flips our own hiddenLabels state rather than calling
                // chart.setDatasetVisibility directly — that approach broke
                // whenever "Compare last year" rebuilt the datasets array
                // (see hiddenLabels' definition above). This is a plain state
                // update; the `hidden` flag set on each dataset above is what
                // actually drives visibility on the next render.
                onClick: (_event, legendItem) => {
                  const label = legendItem.text;
                  setHiddenLabels((prev) => {
                    const next = new Set(prev);
                    if (next.has(label)) next.delete(label);
                    else next.add(label);
                    return next;
                  });
                },
              },
            },
            scales: {
              y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` } },
            },
          }}
        />
      </div>
    </section>
  );
}
